import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GraduationCap, Eye, EyeSlash, Spinner, Buildings, Wrench, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [detectedRole, setDetectedRole] = useState('client_external');
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const checkDomain = useCallback(async (emailValue) => {
    if (!emailValue || !emailValue.includes('@') || emailValue.endsWith('@')) return;
    setIsCheckingDomain(true);
    try {
      const response = await axios.get(`${API_URL}/api/domains/check?email=${encodeURIComponent(emailValue)}`);
      setDetectedRole(response.data.role);
    } catch {
      setDetectedRole('client_external');
    } finally {
      setIsCheckingDomain(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkDomain(email);
    }, 500);
    return () => clearTimeout(timer);
  }, [email, checkDomain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas nao coincidem');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    const result = await register(email, password, name, detectedRole);
    
    if (result.success) {
      navigate('/my-learning', { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-[#2C3E50]/10 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#27AE60] flex items-center justify-center mx-auto mb-4">
                <GraduationCap size={32} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-[#1A252F]">Criar Conta</h1>
              <p className="text-[#5C6B7A] mt-2">Comece sua jornada de aprendizado hoje</p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-lg bg-[#E74C3C]/10 border border-[#E74C3C]/20" data-testid="register-error">
                <p className="text-sm text-[#E74C3C]">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-[#1A252F] font-medium">
                  Nome Completo
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Joao Silva"
                  className="mt-2 bg-white border-[#BDC3C7] focus:border-[#2C3E50] focus:ring-[#2C3E50]"
                  required
                  data-testid="register-name"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[#1A252F] font-medium">
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@exemplo.com"
                  className="mt-2 bg-white border-[#BDC3C7] focus:border-[#2C3E50] focus:ring-[#2C3E50]"
                  required
                  data-testid="register-email"
                />
              </div>

              {/* Role indicator - automatic based on email domain */}
              <div 
                className={`rounded-xl border-2 p-4 transition-all duration-300 ${
                  detectedRole === 'client_internal'
                    ? 'border-[#2980B9] bg-[#2980B9]/5'
                    : 'border-[#BDC3C7] bg-[#F8F9FA]'
                }`}
                data-testid="role-indicator"
              >
                <div className="flex items-center gap-3">
                  {isCheckingDomain ? (
                    <Spinner size={20} className="animate-spin text-[#5C6B7A]" />
                  ) : detectedRole === 'client_internal' ? (
                    <div className="w-10 h-10 rounded-lg bg-[#2980B9]/10 flex items-center justify-center flex-shrink-0">
                      <Wrench size={20} className="text-[#2980B9]" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#5C6B7A]/10 flex items-center justify-center flex-shrink-0">
                      <Buildings size={20} className="text-[#5C6B7A]" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold ${
                        detectedRole === 'client_internal' ? 'text-[#2980B9]' : 'text-[#5C6B7A]'
                      }`}>
                        {detectedRole === 'client_internal' ? 'Cliente Interno' : 'Cliente Externo'}
                      </p>
                      {detectedRole === 'client_internal' && (
                        <CheckCircle size={16} weight="fill" className="text-[#2980B9]" />
                      )}
                    </div>
                    <p className="text-xs text-[#5C6B7A] mt-0.5">
                      {detectedRole === 'client_internal' 
                        ? 'Dominio reconhecido - acesso a Modo Supervisor e Manutencao' 
                        : 'Acesso a cursos de operacao de equipamentos'}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-[#1A252F] font-medium">
                  Senha
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Pelo menos 6 caracteres"
                    className="bg-white border-[#BDC3C7] focus:border-[#2C3E50] focus:ring-[#2C3E50] pr-10"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B7A] hover:text-[#2C3E50]"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-[#1A252F] font-medium">
                  Confirmar Senha
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita sua senha"
                  className="mt-2 bg-white border-[#BDC3C7] focus:border-[#2C3E50] focus:ring-[#2C3E50]"
                  required
                  data-testid="register-confirm-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#27AE60] text-white hover:bg-[#219653] py-6 font-medium"
                data-testid="register-submit"
              >
                {loading ? (
                  <Spinner size={20} className="animate-spin" />
                ) : (
                  'Criar Conta'
                )}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-[#5C6B7A]">
                Ja tem uma conta?{' '}
                <Link 
                  to="/login" 
                  className="text-[#2C3E50] hover:underline font-medium"
                  data-testid="login-link"
                >
                  Entrar
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
